import React, { useEffect, useRef, useState } from 'react';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const Alert = ({
  isOpen,
  onClose,
  title = '',
  message = '',
  type = 'info',
  onConfirm,
  confirmText = 'OK',
  showCancel = false,
  cancelText = 'Cancel',
  onCancel,
  autoClose = 5000
}) => {
  const [progress, setProgress] = useState(100);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const progressInterval = useRef(null);

  useEffect(() => {
    if (isOpen && autoClose) {
      setProgress(100);
      startTimeRef.current = Date.now();
      
      progressInterval.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        const remaining = Math.max(0, autoClose - elapsed);
        setProgress((remaining / autoClose) * 100);
        
        if (remaining <= 0) {
          clearInterval(progressInterval.current);
          onClose();
        }
      }, 50);

      timerRef.current = setTimeout(() => {
        onClose();
      }, autoClose);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressInterval.current) clearInterval(progressInterval.current);
    };
  }, [isOpen, autoClose, onClose]);

  const handleMouseEnter = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (progressInterval.current) clearInterval(progressInterval.current);
    setProgress(100);
  };

  const handleMouseLeave = () => {
    if (autoClose) {
      startTimeRef.current = Date.now();
      progressInterval.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        const remaining = Math.max(0, autoClose - elapsed);
        setProgress((remaining / autoClose) * 100);
        
        if (remaining <= 0) {
          clearInterval(progressInterval.current);
          onClose();
        }
      }, 50);

      timerRef.current = setTimeout(() => {
        onClose();
      }, autoClose);
    }
  };

  if (!isOpen) return null;

  const typeConfigs = {
    info: {
      icon: <FaInfoCircle className="text-blue-500 text-2xl" />,
      bg: 'bg-white/90 backdrop-blur-xl',
      text: 'text-gray-800',
      border: 'border-blue-100',
      button: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 shadow-md shadow-blue-500/20 text-white',
      progress: 'bg-blue-500'
    },
    success: {
      icon: <FaCheckCircle className="text-green-500 text-2xl" />,
      bg: 'bg-white/90 backdrop-blur-xl',
      text: 'text-gray-800',
      border: 'border-green-100',
      button: 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 shadow-md shadow-green-500/20 text-white',
      progress: 'bg-green-500'
    },
    warning: {
      icon: <FaExclamationTriangle className="text-amber-500 text-2xl" />,
      bg: 'bg-white/90 backdrop-blur-xl',
      text: 'text-gray-800',
      border: 'border-amber-100',
      button: 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 shadow-md shadow-amber-500/20 text-white',
      progress: 'bg-amber-500'
    },
    error: {
      icon: <FaExclamationTriangle className="text-red-500 text-2xl" />,
      bg: 'bg-white/90 backdrop-blur-xl',
      text: 'text-gray-800',
      border: 'border-red-100',
      button: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-md shadow-red-500/20 text-white',
      progress: 'bg-red-500'
    }
  };

  const config = typeConfigs[type] || typeConfigs.info;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95, transition: { duration: 0.15 } }}
            transition={{ 
              type: 'spring',
              damping: 25,
              stiffness: 400
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`relative w-full max-w-md rounded-2xl shadow-2xl ${config.bg} ${config.border} border-2 overflow-hidden pointer-events-auto transform transition-all ring-1 ring-black/5`}
            role="alertdialog"
            aria-labelledby="alert-title"
            aria-describedby="alert-description"
          >
            {/* Progress bar */}
            {autoClose && (
              <div className="h-1 w-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                <motion.div 
                  className={`h-full ${config.progress}`}
                  initial={{ width: '100%' }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: autoClose / 1000, ease: 'linear' }}
                />
              </div>
            )}
            
            <div className="p-5 sm:p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {config.icon}
                </div>
                <div className="ml-4 flex-1">
                  {title && (
                    <h3 
                      id="alert-title"
                      className={`text-lg font-semibold ${config.text}`}
                    >
                      {title}
                    </h3>
                  )}
                  <div className="mt-1">
                    <p 
                      id="alert-description"
                      className={`text-sm ${config.text} opacity-90`}
                    >
                      {message}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md"
                  aria-label="Close"
                >
                  <span className="sr-only">Close</span>
                  <FaTimes className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              
              <div className="mt-5 sm:mt-6 flex flex-col-reverse sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                {showCancel && (
                  <button
                    type="button"
                    onClick={onCancel || onClose}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                  >
                    {cancelText}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (onConfirm) onConfirm();
                    onClose();
                  }}
                  className={`w-full sm:w-auto px-4 py-2 text-sm font-medium text-white ${config.button} rounded-md border border-transparent shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200`}
                >
                  {confirmText}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default Alert;
